import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { organizationAPI } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { toast } from 'sonner';
import {
  Plus,
  Pencil,
  Trash2,
  Building2,
  Loader2,
  Search,
  Upload,
  ImageIcon,
} from 'lucide-react';
import api from '@/lib/api';

// ───────────────────────────────────────────────────────────
// LetterheadPreview — live mock of the generated DOCX letterhead.
// Mirrors the Word section header/footer used by the backend.
// ───────────────────────────────────────────────────────────
function LetterheadPreview({ data, logoUrl }) {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const fullLogoSrc = logoUrl ? `${backendUrl}${logoUrl}` : null;
  const name = data.name?.trim() || 'Your Organization';
  const addr = data.address?.trim() || 'Your registered address will appear here';
  const contactParts = [data.contact_phone, data.contact_email, data.website].filter(Boolean);
  const headerText = data.header_text?.trim();
  const footerText = data.footer_text?.trim() || `${name} — Confidential`;
  const cin = data.cin?.trim();
  const today = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' });

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Live Preview</h4>
        <span className="text-[10px] text-muted-foreground">Updates as you type</span>
      </div>
      {/* A4-ish mock page */}
      <div
        className="bg-white border border-slate-300 shadow-md rounded-sm overflow-hidden flex flex-col"
        style={{ aspectRatio: '210 / 297', fontFamily: 'Calibri, Segoe UI, Arial, sans-serif' }}
        data-testid="letterhead-preview"
      >
        {/* Letterhead — mirrors Word section header */}
        <div className="px-4 pt-3 pb-2 border-b border-slate-200 flex items-start gap-3">
          <div className="w-[28%] flex items-center justify-start">
            {fullLogoSrc ? (
              <img src={fullLogoSrc} alt="Logo" className="max-h-12 max-w-full object-contain" />
            ) : (
              <div className="w-12 h-12 rounded bg-gradient-to-br from-blue-700 to-blue-500 text-white flex items-center justify-center font-bold text-lg">
                {(name[0] || '?').toUpperCase()}
              </div>
            )}
          </div>
          <div className="flex-1 text-right leading-tight">
            <div className="font-bold text-[11px] text-blue-900 truncate" title={name}>{name}</div>
            <div className="text-[8px] text-slate-600 mt-0.5" style={{ lineHeight: 1.3 }}>{addr}</div>
            {contactParts.length > 0 && (
              <div className="text-[8px] text-blue-700 mt-0.5">{contactParts.join('  ·  ')}</div>
            )}
          </div>
        </div>

        {/* Body sample */}
        <div className="flex-1 px-5 py-3 text-[8.5px] text-slate-700 overflow-hidden">
          {headerText && (
            <div className="italic text-slate-500 mb-2 text-[7.5px]">{headerText}</div>
          )}
          <div className="mb-2">
            <span className="font-semibold">Date:</span> {today} &nbsp;&nbsp; <span className="text-slate-500">Ref: BLG/OL/2026/001</span>
          </div>
          <div className="mb-1">To,</div>
          <div className="font-semibold mb-2">[Candidate Name]</div>
          <div className="font-semibold text-blue-900 mb-2 text-[9px]">Subject: Offer of Employment — [Designation]</div>
          <div className="mb-2">Dear [Candidate],</div>
          <div className="mb-2 leading-relaxed">
            We are pleased to extend an offer of employment to you for the position of [Designation] with{' '}
            <span className="font-semibold">{name}</span>…
          </div>
          {/* tiny mock table */}
          <div className="border border-slate-300 mt-3">
            <div className="bg-blue-900 text-white text-[7px] font-bold px-2 py-1">COMPENSATION &amp; ROLE DETAILS</div>
            <div className="grid grid-cols-[35%_1fr] text-[7.5px]">
              <div className="px-2 py-1 bg-slate-50 font-semibold border-t border-slate-200">Designation</div>
              <div className="px-2 py-1 border-t border-l border-slate-200">[Designation]</div>
              <div className="px-2 py-1 bg-slate-50 font-semibold border-t border-slate-200">Monthly Salary</div>
              <div className="px-2 py-1 border-t border-l border-slate-200">₹[Amount] — as per Annexure A</div>
            </div>
          </div>
        </div>

        {/* Footer — mirrors Word section footer */}
        <div className="px-3 py-1.5 border-t border-slate-200 bg-slate-50 text-center text-[7px] text-slate-400 leading-tight">
          {[footerText, cin ? `CIN: ${cin}` : null].filter(Boolean).join(' · ')}
        </div>
      </div>
      <div className="text-[10px] text-muted-foreground italic leading-snug">
        Sample showing how your letterhead will appear on the first page of every generated document. Logo loads after upload.
      </div>
    </div>
  );
}

export default function OrganizationsPage() {
  const { canManageOrgs } = useAuth();
  // canManageOrgs() === can create/delete orgs (Admin level). LOP Manager has org:edit but NOT org:create.
  const canCreateOrDelete = typeof canManageOrgs === 'function' ? canManageOrgs() : false;
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [formData, setFormData] = useState({
    name: '', address: '', epf_enabled: false,
    authorized_person_name: '', authorized_person_position: '',
    website: '', contact_email: '', contact_phone: '',
    cin: '', city: '', header_text: '', footer_text: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadOrganizations();
  }, []);

  const loadOrganizations = async () => {
    try {
      const response = await organizationAPI.getAll();
      setOrganizations(response.data);
    } catch (error) {
      toast.error('Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (org = null) => {
    if (org) {
      setEditingOrg(org);
      setFormData({
        name: org.name,
        address: org.address || '',
        epf_enabled: org.epf_enabled || false,
        authorized_person_name: org.authorized_person_name || '',
        authorized_person_position: org.authorized_person_position || '',
        website: org.website || '',
        contact_email: org.contact_email || '',
        contact_phone: org.contact_phone || '',
        cin: org.cin || '',
        city: org.city || '',
        header_text: org.header_text || '',
        footer_text: org.footer_text || '',
      });
    } else {
      setEditingOrg(null);
      setFormData({
        name: '', address: '', epf_enabled: false,
        authorized_person_name: '', authorized_person_position: '',
        website: '', contact_email: '', contact_phone: '',
        cin: '', city: '', header_text: '', footer_text: '',
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingOrg) {
        await organizationAPI.update(editingOrg.id, formData);
        toast.success('Organization updated successfully');
      } else {
        await organizationAPI.create(formData);
        toast.success('Organization created successfully');
      }
      setDialogOpen(false);
      loadOrganizations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (org) => {
    try {
      await organizationAPI.delete(org.id);
      toast.success('Organization deleted successfully');
      loadOrganizations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  const handleLogoUpload = async (orgId, file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      await api.post(`/organizations/${orgId}/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Logo uploaded successfully');
      loadOrganizations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Logo upload failed');
    }
  };

  const handleSignatureUpload = async (orgId, file) => {
    if (!file) return;
    if (!orgId) { toast.error('Save the organization first, then upload the signature.'); return; }
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post(`/organizations/${orgId}/signature`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Signature uploaded');
      // Reflect immediately in the live preview / editing org
      if (editingOrg && editingOrg.id === orgId) {
        setEditingOrg({ ...editingOrg, signature_url: res.data?.signature_url || `/api/organizations/${orgId}/signature` });
      }
      loadOrganizations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Signature upload failed');
    }
  };

  const handleSignatureDelete = async (orgId) => {
    if (!orgId) return;
    if (!window.confirm('Remove the saved signature?')) return;
    try {
      await api.delete(`/organizations/${orgId}/signature`);
      toast.success('Signature removed');
      if (editingOrg && editingOrg.id === orgId) {
        setEditingOrg({ ...editingOrg, signature_url: null });
      }
      loadOrganizations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove signature');
    }
  };

  const filteredOrgs = organizations.filter((org) =>
    org.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (org.address && org.address.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <DashboardLayout>
      <div data-testid="organizations-page" className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Organizations</h1>
            <p className="text-muted-foreground mt-1">
              Manage your organizations
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            {canCreateOrDelete && (
              <DialogTrigger asChild>
                <Button onClick={() => handleOpenDialog()} data-testid="add-org-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Organization
                </Button>
              </DialogTrigger>
            )}
            <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingOrg ? 'Edit Organization' : 'Add Organization'}
                </DialogTitle>
                <DialogDescription>
                  Branding details (logo, address, header, footer) are used on every generated document — Offer Letters, Salary Slips, Increments, Addendums.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 py-4">
                  {/* ── LEFT: form fields ── */}
                  <div className="space-y-5">
                  {/* ── Basic ── */}
                  <div className="space-y-3">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground border-b pb-1">Basic Information</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label htmlFor="name" className="text-xs">Organization Name *</Label>
                        <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="org-name-input" placeholder="Acme Pvt Ltd" />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="city" className="text-xs">City (Place on signatures)</Label>
                        <Input id="city" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} placeholder="Noida" />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="address" className="text-xs">Address</Label>
                      <Textarea id="address" rows={2} value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} data-testid="org-address-input" placeholder="Plot no: A-40, Unit No: 207, 2nd Floor, I-Thum Tower-A, Sector 62, Noida - 201309" />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs">Phone</Label>
                        <Input value={formData.contact_phone} onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })} placeholder="+91 ..." />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Email</Label>
                        <Input type="email" value={formData.contact_email} onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })} placeholder="hr@acme.com" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Website</Label>
                        <Input value={formData.website} onChange={(e) => setFormData({ ...formData, website: e.target.value })} placeholder="www.acme.com" />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">CIN / Registration No.</Label>
                      <Input value={formData.cin} onChange={(e) => setFormData({ ...formData, cin: e.target.value })} placeholder="U72200DL2018PTC123456" />
                    </div>
                  </div>

                  {/* ── Authorized Signatory ── */}
                  <div className="space-y-3">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground border-b pb-1">Authorized Signatory</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs">Name</Label>
                        <Input value={formData.authorized_person_name} onChange={(e) => setFormData({ ...formData, authorized_person_name: e.target.value })} placeholder="Nayan Verma" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Position</Label>
                        <Input value={formData.authorized_person_position} onChange={(e) => setFormData({ ...formData, authorized_person_position: e.target.value })} placeholder="Director" />
                      </div>
                    </div>

                    {/* Signature image upload */}
                    <div className="space-y-1.5">
                      <Label className="text-xs flex items-center justify-between">
                        <span>Signature Image</span>
                        <span className="text-[10px] text-muted-foreground font-normal">PNG/JPG with transparent background recommended · max 1 MB</span>
                      </Label>
                      <div className="flex items-center gap-3 rounded-md border border-dashed bg-muted/30 p-3">
                        {editingOrg?.signature_url ? (
                          <img
                            src={`${process.env.REACT_APP_BACKEND_URL}${editingOrg.signature_url}?t=${Date.now()}`}
                            alt="Signature"
                            className="max-h-16 max-w-[180px] object-contain bg-white rounded border px-2 py-1"
                            data-testid="org-signature-preview"
                          />
                        ) : (
                          <div className="h-16 w-[180px] flex items-center justify-center text-[11px] text-muted-foreground italic border rounded bg-white">
                            No signature uploaded
                          </div>
                        )}
                        <div className="flex flex-col gap-2">
                          <label className="inline-flex">
                            <Button type="button" variant="outline" size="sm" asChild={false} onClick={() => document.getElementById(`sig-upload-${editingOrg?.id || 'new'}`)?.click()} data-testid="org-signature-upload-btn">
                              {editingOrg?.signature_url ? 'Replace' : 'Upload signature'}
                            </Button>
                            <input
                              id={`sig-upload-${editingOrg?.id || 'new'}`}
                              type="file"
                              accept="image/png,image/jpeg,image/webp"
                              className="hidden"
                              onChange={(e) => handleSignatureUpload(editingOrg?.id, e.target.files?.[0])}
                            />
                          </label>
                          {editingOrg?.signature_url && (
                            <Button type="button" variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => handleSignatureDelete(editingOrg.id)} data-testid="org-signature-remove-btn">
                              Remove
                            </Button>
                          )}
                        </div>
                      </div>
                      <p className="text-[11px] text-muted-foreground">
                        Appears between <strong>For {formData.name || '[Organization]'}</strong> and the signatory name on every generated letter.
                      </p>
                    </div>
                  </div>

                  {/* ── Document Branding ── */}
                  <div className="space-y-3">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground border-b pb-1">Document Header &amp; Footer</h4>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Header Text <span className="text-muted-foreground font-normal">(optional banner above every letter)</span></Label>
                      <Textarea rows={2} value={formData.header_text} onChange={(e) => setFormData({ ...formData, header_text: e.target.value })} placeholder="An ISO 9001:2015 certified company  ·  GST: 09AAAAA0000A1Z5" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Footer Text <span className="text-muted-foreground font-normal">(small print at the bottom of every page)</span></Label>
                      <Textarea rows={2} value={formData.footer_text} onChange={(e) => setFormData({ ...formData, footer_text: e.target.value })} placeholder="This document is confidential and intended only for the named addressee." />
                    </div>
                  </div>

                  {/* ── EPF ── */}
                  <div className="flex items-center justify-between py-1 border-t pt-3">
                    <div>
                      <Label htmlFor="epf_enabled" className="text-sm font-medium">EPF Enabled</Label>
                      <p className="text-xs text-muted-foreground">Enable PF deduction (fixed ₹1,800) for this organization</p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={formData.epf_enabled}
                      onClick={() => setFormData({ ...formData, epf_enabled: !formData.epf_enabled })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${formData.epf_enabled ? 'bg-emerald-600' : 'bg-gray-200 dark:bg-gray-700'}`}
                      data-testid="epf-toggle"
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.epf_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>
                  </div>{/* /LEFT col */}

                  {/* ── RIGHT: Live preview ── */}
                  <LetterheadPreview data={formData} logoUrl={editingOrg?.logo_url} />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting} data-testid="org-submit-btn">
                    {submitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 spinner" />
                        Saving...
                      </>
                    ) : editingOrg ? (
                      'Update'
                    ) : (
                      'Create'
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search organizations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="org-search-input"
          />
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 spinner text-muted-foreground" />
              </div>
            ) : filteredOrgs.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <Building2 className="h-12 w-12 text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">
                  {searchQuery ? 'No organizations found' : 'No organizations yet'}
                </p>
                {!searchQuery && canCreateOrDelete && (
                  <Button
                    variant="link"
                    onClick={() => handleOpenDialog()}
                    className="mt-2"
                  >
                    Create your first organization
                  </Button>
                )}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Logo</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Address</TableHead>
                    <TableHead>EPF</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOrgs.map((org) => (
                    <TableRow key={org.id} className="table-row-hover" data-testid={`org-row-${org.id}`}>
                      <TableCell>
                        {org.logo_url ? (
                          <img src={`${process.env.REACT_APP_BACKEND_URL}${org.logo_url}`} alt="Logo" className="h-8 w-auto max-w-[80px] object-contain" />
                        ) : (
                          <label className="cursor-pointer inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
                            <Upload className="h-3.5 w-3.5" />
                            <span>Upload</span>
                            <input type="file" accept="image/*" className="hidden" onChange={e => handleLogoUpload(org.id, e.target.files[0])} />
                          </label>
                        )}
                      </TableCell>
                      <TableCell className="font-medium">{org.name}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {org.address || '-'}
                      </TableCell>
                      <TableCell>
                        {org.epf_enabled ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">Enabled</span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">Off</span>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground font-mono text-sm">
                        {formatDate(org.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {org.logo_url && (
                            <label className="cursor-pointer">
                              <Button variant="ghost" size="icon" asChild><span><ImageIcon className="h-4 w-4" /></span></Button>
                              <input type="file" accept="image/*" className="hidden" onChange={e => handleLogoUpload(org.id, e.target.files[0])} />
                            </label>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenDialog(org)}
                            data-testid={`edit-org-${org.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          {canCreateOrDelete && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="text-destructive hover:text-destructive"
                                  data-testid={`delete-org-${org.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Organization</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to delete "{org.name}"? This will also delete all associated verticals and employees. This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDelete(org)}
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    data-testid={`confirm-delete-org-${org.id}`}
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
